import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  BookOpenCheck, 
  CheckCircle, 
  Calendar, 
  Clock,
  Search,
  Filter,
  ExternalLink,
  Video,
  FileText,
  Brain,
  Loader2,
  ArrowLeft,
  Play
} from 'lucide-react';
import Navigation from './Navigation';

const HomeworkPage = () => {
  const [homework, setHomework] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCompleted, setShowCompleted] = useState(true);
  const [weekFilter, setWeekFilter] = useState('all');
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [completing, setCompleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchHomework();
  }, []);

  const fetchHomework = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/homework`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHomework(data.data.homework || []);
      }
    } catch (error) {
      console.error('Error fetching homework:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleMarkComplete = async (homeworkId) => {
    try {
      setCompleting(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/homework/${homeworkId}/complete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Trigger confetti animation
        triggerConfetti();
        // Update local state
        setHomework(prev => prev.map(hw => 
          hw.id === homeworkId ? { ...hw, is_completed: true } : hw
        ));
        if (selectedHomework?.id === homeworkId) {
          setSelectedHomework(prev => ({ ...prev, is_completed: true }));
        }
      }
    } catch (error) {
      console.error('Error marking homework complete:', error);
    } finally {
      setCompleting(false);
    }
  };

  const triggerConfetti = () => {
    // Simple confetti effect using CSS animations
    const confetti = document.createElement('div');
    confetti.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
    `;
    
    // Create confetti particles
    for (let i = 0; i < 50; i++) {
      const particle = document.createElement('div');
      particle.style.cssText = `
        position: absolute;
        width: 10px;
        height: 10px;
        background: ${['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][Math.floor(Math.random() * 5)]};
        left: ${Math.random() * 100}%;
        top: -10px;
        animation: confetti-fall 3s linear forwards;
        border-radius: 50%;
      `;
      confetti.appendChild(particle);
    }
    
    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes confetti-fall {
        0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
        100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(confetti);
    
    // Remove after animation
    setTimeout(() => {
      document.body.removeChild(confetti);
      document.head.removeChild(style);
    }, 3000);
  };

  const getContentIcon = (linkType) => {
    switch (linkType) {
      case 'video': return <Video className="w-4 h-4" />;
      case 'resource': return <FileText className="w-4 h-4" />;
      case 'prompt': return <Brain className="w-4 h-4" />;
      case 'external': return <ExternalLink className="w-4 h-4" />;
      default: return <BookOpenCheck className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const filteredHomework = homework.filter(hw => {
    // Search filtering
    const matchesSearch = !searchTerm || 
      hw.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      hw.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Completion filtering  
    const matchesCompletion = showCompleted || !hw.is_completed;
    
    // Week filtering
    const matchesWeek = weekFilter === 'all' || 
      (weekFilter === 'thisWeek' && isThisWeek(hw.week_of)) ||
      (weekFilter === 'lastWeek' && isLastWeek(hw.week_of)) ||
      (weekFilter === 'thisMonth' && isThisMonth(hw.week_of));
      
    return matchesSearch && matchesCompletion && matchesWeek;
  });

  const isThisWeek = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
    const endOfWeek = new Date(now.setDate(now.getDate() - now.getDay() + 6));
    return date >= startOfWeek && date <= endOfWeek;
  };

  const isLastWeek = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const startOfLastWeek = new Date(now.setDate(now.getDate() - now.getDay() - 7));
    const endOfLastWeek = new Date(now.setDate(now.getDate() - now.getDay() - 1));
    return date >= startOfLastWeek && date <= endOfLastWeek;
  };

  const isThisMonth = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  };

  const handleContentLinkClick = (link) => {
    switch(link.link_type) {
      case 'video':
        // Navigate to video library and open specific video
        navigate(`/videos?video=${link.content_id}`);
        break;
      case 'resource': 
        // Handle resource links - guides use slug, others use content_id
        if (link.resource_content_type === 'guide' && link.resource_slug) {
          // Navigate to guide view using slug
          navigate(`/resources/${link.resource_slug}`);
        } else if (link.resource_content_type === 'link' && link.external_url) {
          // For link resources, open external URL
          window.open(link.external_url, '_blank');
        } else {
          // Fallback: navigate to resources page (could filter by ID if needed)
          navigate('/resources');
        }
        break;
      case 'prompt':
        // Navigate to prompt library
        navigate('/prompts');
        break;
      case 'external':
        // Open in new tab
        window.open(link.external_url, '_blank');
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  if (selectedHomework) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          {/* Back Button */}
          <Button 
            variant="outline" 
            onClick={() => setSelectedHomework(null)}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Homework List
          </Button>

          {/* Homework Detail */}
          <Card className="rounded-3xl shadow-lg border-0 bg-white">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-3xl text-gray-900 mb-2">
                    {selectedHomework.title}
                  </CardTitle>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>Week of {formatDate(selectedHomework.week_of)}</span>
                    </div>
                    {selectedHomework.due_date && (
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>Due {formatDate(selectedHomework.due_date)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {selectedHomework.is_completed ? (
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Complete
                    </Badge>
                  ) : (
                    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                      <Clock className="w-3 h-3 mr-1" />
                      Pending
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="prose max-w-none mb-8">
                <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">
                  {selectedHomework.description}
                </p>
              </div>

              {selectedHomework.content_links && selectedHomework.content_links.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Resources & Links</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedHomework.content_links.map((link, index) => (
                      <div 
                        key={index} 
                        className="p-4 border border-gray-200 rounded-2xl hover:bg-gray-50 cursor-pointer transition-all duration-200"
                        onClick={() => handleContentLinkClick(link)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            {getContentIcon(link.link_type)}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 mb-1">{link.link_text}</h4>
                            {link.description && (
                              <p className="text-sm text-gray-600">{link.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                {!selectedHomework.is_completed && (
                  <Button 
                    onClick={() => handleMarkComplete(selectedHomework.id)}
                    disabled={completing}
                    className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-8 py-3 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {completing ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-5 h-5 mr-2" />
                    )}
                    {completing ? 'Marking Complete...' : 'Mark as Complete'}
                  </Button>
                )}
                {selectedHomework.is_completed && (
                  <div className="flex items-center space-x-2 bg-green-100 text-green-800 px-6 py-3 rounded-2xl">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Assignment Complete!</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <BookOpenCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">
                Homework Assignments
              </h1>
              <p className="text-lg text-gray-600">
                Complete your weekly assignments to accelerate your impact
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-8 rounded-2xl shadow-md">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search homework assignments..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex gap-4">
                <select
                  value={weekFilter}
                  onChange={(e) => setWeekFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Weeks</option>
                  <option value="thisWeek">This Week</option>
                  <option value="lastWeek">Last Week</option>
                  <option value="thisMonth">This Month</option>
                </select>
                
                <label className="flex items-center space-x-2 px-4 py-3 border border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={showCompleted}
                    onChange={(e) => setShowCompleted(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Show Completed</span>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Homework List */}
        {filteredHomework.length === 0 ? (
          <Card className="rounded-3xl shadow-lg border-0 bg-white">
            <CardContent className="pt-12 pb-12">
              <div className="text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <BookOpenCheck className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No homework found</h3>
                <p className="text-gray-600">
                  {searchTerm || weekFilter !== 'all' || !showCompleted 
                    ? 'Try adjusting your search or filters' 
                    : 'No homework assignments have been published yet'
                  }
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredHomework.map((hw) => (
              <Card 
                key={hw.id} 
                className="rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                onClick={() => setSelectedHomework(hw)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg text-gray-900 mb-2 line-clamp-2">
                        {hw.title}
                      </CardTitle>
                      <div className="flex items-center space-x-3 text-sm text-gray-600">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(hw.week_of)}</span>
                        </div>
                        {hw.due_date && (
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>Due {formatDate(hw.due_date)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {hw.is_completed ? (
                      <Badge className="bg-green-100 text-green-800 border-green-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Complete
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {truncateText(hw.description, 120)}
                  </p>
                  
                  {hw.content_links && hw.content_links.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-700 mb-2">
                        {hw.content_links.length} resource{hw.content_links.length !== 1 ? 's' : ''}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {hw.content_links.slice(0, 3).map((link, index) => (
                          <div key={index} className="flex items-center space-x-1 bg-gray-100 px-2 py-1 rounded-lg text-xs text-gray-600">
                            {getContentIcon(link.link_type)}
                            <span>{truncateText(link.link_text, 15)}</span>
                          </div>
                        ))}
                        {hw.content_links.length > 3 && (
                          <div className="bg-gray-100 px-2 py-1 rounded-lg text-xs text-gray-500">
                            +{hw.content_links.length - 3}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <Button 
                    variant="outline" 
                    className="w-full rounded-xl"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedHomework(hw);
                    }}
                  >
                    View Assignment
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default HomeworkPage;
